import React from 'react';

import AnalyticsWebClient, { originType } from '@atlassiansox/analytics-web-client';
import type { EntryPointProps } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import { token } from '@atlaskit/tokens';
import { EMBEDDED_CONFLUENCE_MODE, Page, Whiteboard } from '@atlassian/embedded-confluence';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { PARENT_PRODUCTS } from '../../common/constants';
import { getWhiteboardUrlMatch } from '../../utils/whiteboard';

import { getAnalyticsEnv, onEditMode } from './utils';

export interface ConfluencePagePreviewPanelProps {
	title: string;
	url: string;
	parentProduct: (typeof PARENT_PRODUCTS)[keyof typeof PARENT_PRODUCTS];
}

export type ConfluencePagePreviewPanelEntrypointProps = EntryPointProps<
	Record<string, never>,
	Record<string, never>,
	ConfluencePagePreviewPanelProps,
	Record<string, never>
>;

const styles = cssMap({
	body: {
		flexGrow: 1,
		overflowY: 'auto',
		paddingInlineStart: token('space.300'),
		paddingBlockStart: token('space.300'),
	},
});

const ConfluencePagePreviewPanel = ({
	props: { title, url, parentProduct },
}: ConfluencePagePreviewPanelEntrypointProps) => {
	const { close, setPanelWidth } = usePreviewPanelsActions();
	const [analyticsClient, setAnalyticsClient] = React.useState<AnalyticsWebClient>();

	React.useEffect(() => {
		setAnalyticsClient(
			new AnalyticsWebClient({
				product: 'confluencePagePreviewPanel',
				env: getAnalyticsEnv(window.location.hostname),
				origin: originType.WEB,
			}),
		);
	}, []);

	const onModeChange = (mode: EMBEDDED_CONFLUENCE_MODE) => onEditMode(mode, setPanelWidth);

	const onClose = () => {
		close();
	};

	const whiteboardMatch = getWhiteboardUrlMatch(url);
	const content = whiteboardMatch ? (
		<Whiteboard
			contentId={whiteboardMatch.params.contentId}
			spaceKey={whiteboardMatch.params.spaceKey}
			parentProduct={parentProduct}
			hasFooterLogo={false}
			shouldShowFooter={false}
			embeddedConfluenceSource="confluence-page-preview-panel"
		/>
	) : (
		<Page
			url={url}
			showFooter={false}
			hasFooterLogo={false}
			onModeChange={onModeChange}
			parentProduct={parentProduct}
			embeddedConfluenceSource="confluence-page-preview-panel"
		/>
	);

	return (
		<PreviewPanel testId="confluence-preview-panel">
			<PreviewPanelHeader
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType="confluence"
				onClose={onClose}
				url={url}
			>
				<PreviewPanelTitle title={title} icon={<PageIcon label="" size="small" />} />
			</PreviewPanelHeader>
			<PreviewPanelBody
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType="confluence"
				styles={styles.body}
			>
				{content}
			</PreviewPanelBody>
		</PreviewPanel>
	);
};

export default ConfluencePagePreviewPanel;
